import { Injectable } from '@angular/core';
import { resolve } from '../../api/gqty';
import { readDeleteAccountError, throwIfDeleteBlocked } from '../settings/account-actions';

/**
 * Returns an i18n key when the email mutation reported any error, or `null`
 * when there were none. There is no per-`__typename` mapping yet — every
 * failure surfaces the same generic message — but `__typename` is still read
 * so gqty includes the errors in the selection.
 */
function firstEmailError(
  errors: readonly { __typename?: string | null }[] | null | undefined,
): string | null {
  const first = errors?.[0];
  if (!first) {
    return null;
  }
  void first.__typename;
  return 'profile.email.errors.changeFailed';
}

/** The current user's profile, as shown on the profile page. */
export interface Profile {
  id: string;
  email: string;
  /** A requested new email awaiting confirmation, or null when none is pending. */
  unconfirmedEmail: string | null;
  firstName: string;
  lastName: string;
  name: string;
  roles: string[];
  status: string;
}

@Injectable({ providedIn: 'root' })
export class ProfileService {
  /** Load the current user's profile via the `me` query. */
  async getProfile(): Promise<Profile | null> {
    return resolve(
      ({ query }) => {
        const me = query.me;
        if (!me) return null;
        // Access every field unconditionally so gqty keeps them in the query.
        const id = me.id;
        const email = me.email;
        const unconfirmedEmail = me.unconfirmedEmail;
        const firstName = me.firstName;
        const lastName = me.lastName;
        const name = me.name;
        const roles = me.roles;
        const status = me.status;
        return {
          id,
          email: email ?? '',
          unconfirmedEmail: unconfirmedEmail ?? null,
          firstName: firstName ?? '',
          lastName: lastName ?? '',
          name: name ?? '',
          roles: [...roles],
          status: status ?? '',
        } satisfies Profile;
      },
      { cachePolicy: 'no-cache' }
    );
  }

  /** Load the tenant's friendly name (read-only, shown on the profile page). */
  async getTenantName(): Promise<string> {
    // tenantSettings is admin-only: for any other role the name is simply absent, not an error.
    try {
      const name = await resolve(
        ({ query }) => query.tenantSettings?.tenantName ?? null,
        { cachePolicy: 'no-cache' }
      );
      return name ?? '';
    } catch {
      return '';
    }
  }

  /** Update the current user's personal data (first/last name). */
  async updatePersonalData(
    id: string,
    firstName: string,
    lastName: string
  ): Promise<{ firstName: string; lastName: string }> {
    return resolve(
      ({ mutation }) => {
        const result = mutation.updateAccount({
          input: { id, firstName, lastName },
        });
        const account = result.account;
        return {
          firstName: account?.firstName ?? firstName,
          lastName: account?.lastName ?? lastName,
        };
      },
      { cachePolicy: 'no-cache' }
    );
  }

  /**
   * Change the current user's password in-app: the backend verifies the
   * current password against Keycloak and sets the new one (no email round
   * trip). Returns an error message key on failure, or `null` on success.
   */
  async changePassword(
    currentPassword: string,
    newPassword: string
  ): Promise<string | null> {
    return resolve(
      ({ mutation }) => {
        const result = mutation.changeMyPassword({
          input: { currentPassword, newPassword },
        });
        const errors = result.errors;
        const first = errors?.[0];
        // Access typename + message unconditionally so gqty resolves them.
        const typename = first?.__typename;
        const message = first?.$on.NotFoundError?.message
          ?? first?.$on.ConflictError?.message
          ?? first?.$on.BadRequestError?.message;
        void result.boolean;
        if (!first) return null;
        // A BadRequestError here means the current password was wrong.
        if (typename === 'BadRequestError') return 'profile.changePassword.errors.wrongCurrent';
        return message ?? 'profile.errors.changePassword';
      },
      { cachePolicy: 'no-cache' }
    );
  }

  /**
   * Request an email-address change: the backend sends a confirmation link to
   * the new address; the change only applies once that link is followed
   * ({@link confirmEmailChange}). Returns an error message key, or `null` on success.
   */
  async requestEmailChange(id: string, email: string): Promise<string | null> {
    return resolve(
      ({ mutation }) => {
        const result = mutation.initUpdateEmailAddress({ input: { id, email } });
        void result.account?.unconfirmedEmail;
        return firstEmailError(result.errors);
      },
      { cachePolicy: 'no-cache' }
    );
  }

  /** Re-point a pending email change to a different address (re-sends the link). */
  async updatePendingEmail(id: string, email: string): Promise<string | null> {
    return resolve(
      ({ mutation }) => {
        const result = mutation.updatePendingAccountEmail({ input: { id, email } });
        void result.account?.unconfirmedEmail;
        return firstEmailError(result.errors);
      },
      { cachePolicy: 'no-cache' }
    );
  }

  /** Confirm a pending email change from the emailed token (public route). */
  async confirmEmailChange(token: string): Promise<string | null> {
    return resolve(
      ({ mutation }) => {
        const result = mutation.confirmEmailAddress({ input: { token } });
        void result.account?.email;
        return firstEmailError(result.errors);
      },
      { cachePolicy: 'no-cache' }
    );
  }

  /**
   * Delete (anonymise) the current user's own account. Throws when the backend
   * blocks the deletion (e.g. the last-admin guard), so the page can toast the
   * reason instead of logging out.
   */
  async deleteAccount(id: string): Promise<void> {
    // Decode inside the resolver (so gqty selects the error fields), then throw
    // AFTER it settles — throwing inside would abort the mutation before any
    // request is sent.
    const info = await resolve(
      ({ mutation }) => {
        const result = mutation.deleteAccount({ input: { id } });
        void result.deleted?.id;
        return readDeleteAccountError(result.errors);
      },
      { cachePolicy: 'no-cache' }
    );
    throwIfDeleteBlocked(info);
  }
}
